// Import dayjs for date manipulations and lodash for string utility functions
import dayjs from "dayjs";
import _ from "lodash";

// Import dayjs plugins for date comparisons
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

// Register plugins with dayjs
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

/**
 * Compares two values based on the provided operator and data type.
 * @param {string} operator - The comparison operator (e.g., ">", "<=", "==", "<>", etc.).
 * @param {string} dataType - The data type of the values being compared ("number", "string", or "date").
 * @param {*} value - The first value for comparison.
 * @param {*} targetValue - The second value (or range for "<>") for comparison.
 * @returns {boolean} - Result of the comparison, defaulting to `true` if inputs are invalid or an error occurs.
 * 
 * Example Usage:
 * compareValues(">", "number", 5, 3);       // true
 * compareValues("<=", "date", "2023-11-01", "2023-11-10"); // true
 */
export default function compareValues(operator, dataType, value, targetValue) {
  
  try {
    // Validate inputs
    if (!operator || !dataType || value === undefined || targetValue === undefined) {
      console.warn("Invalid input parameters:", {
        operator,
        dataType,
        value,
        targetValue,
      });
      return true; // Returning true for undefined behavior
    }

    // Select the appropriate comparison function
    switch (operator) {
      case "<":
      case ">":
      case "<=":
      case ">=":
      case "==":
      case "!=":
        return compareOperator(operator, dataType, value, targetValue);
      case "<>":
        return compareBetween(dataType, value, targetValue);
      default:
        console.warn("Unsupported operator:", operator);
        return true;
    }
  } catch (error) {
    console.error("Error in compareValues function:", error);
    return true; // Fallback return value on error
  }
}

/**
 * Performs operator-based comparisons between two values based on the given data type.
 * @param {string} operator - The comparison operator.
 * @param {string} dataType - The data type ("number", "string", or "date").
 * @param {*} value - The first value to compare.
 * @param {*} targetValue - The second value to compare.
 * @returns {boolean} - Result of the comparison.
 */
export function compareOperator(operator, dataType, value, targetValue) {
  try {
    // Implement comparison logic based on the data type
    switch (dataType) {
      case "number": {
        const operations = {
          ">": value > targetValue,
          ">=": value >= targetValue,
          "<": value < targetValue,
          "<=": value <= targetValue,
          "!=": value !== targetValue,
          "==": value === targetValue,
        };
        return operations[operator] !== undefined ? operations[operator] : true;
      }

      case "string": {
        // Normalize strings for comparison
        const str1 = _.deburr(_.trim(value)).toLowerCase();
        const str2 = _.deburr(_.trim(targetValue)).toLowerCase();
        const operations = {
          ">": str1 > str2,
          ">=": str1 >= str2,
          "<": str1 < str2,
          "<=": str1 <= str2,
          "!=": str1 !== str2,
          "==": str1 === str2,
        };
        return operations[operator] !== undefined ? operations[operator] : true;
      }

      case "date": {
        // Parse dates and compare using dayjs
        const date1 = dayjs(value);
        const date2 = dayjs(targetValue);
        const operations = {
          ">": date1.isAfter(date2),
          ">=": date1.isSame(date2, "day") || date1.isAfter(date2),
          "<": date1.isBefore(date2),
          "<=": date1.isSame(date2, "day") || date1.isBefore(date2),
          "!=": !date1.isSame(date2, "day"),
          "==": date1.isSame(date2, "day"),
        };
        return operations[operator] !== undefined ? operations[operator] : true;
      }

      default:
        console.warn("Unsupported data type:", dataType);
        return true;
    }
  } catch (error) {
    console.error("Error in compareOperator function:", error);
    return true; // Fallback return value on error
  }
}

/**
 * Performs "between" comparison for values within a specified range.
 * @param {string} dataType - The data type ("number", "string", or "date").
 * @param {*} value - The value to check.
 * @param {Array} targetValue - An array containing the minimum and maximum range values.
 * @returns {boolean} - Whether the value is within the specified range.
 * 
 * Example Usage:
 * compareBetween("number", 5, [1, 10]);        // true
 * compareBetween("date", "2023-11-05", ["2023-11-01", "2023-11-10"]); // true
 */
function compareBetween(dataType, value, targetValue) {
 
  try {
    // Check if targetValue has exactly two elements
    if (!Array.isArray(targetValue) || targetValue.length !== 2) {
      console.warn(
        "Invalid targetValue for comparison. Expected an array of two elements:",
        targetValue
      );
      return true;
    }

    const [min, max] = targetValue;

    // Comparison logic for each data type
    const compareFunctions = {
      date: () => {
        return (
          dayjs(value).isSameOrAfter(dayjs(min)) &&
          dayjs(value).isSameOrBefore(dayjs(max))
        );
      },
      number: () => {
        return value >= min && value <= max;
      },
      string: () => {
        const trimmedValue = _.toLower(_.trim(value));
        const trimmedMin = _.toLower(_.trim(min));
        const trimmedMax = _.toLower(_.trim(max));
        return trimmedValue >= trimmedMin && trimmedValue <= trimmedMax;
      },
    };

    // Execute the appropriate comparison function based on type
    const compareFunction = compareFunctions[dataType];

    if (!compareFunction) {
      console.warn("Unsupported data type for comparison:", dataType);
      return true;
    }

    return compareFunction();
  } catch (error) {
    console.error("Error in compareBetween function:", error);
    return true; // Fallback return value on error
  }
}
